namespace advisorop.domains.identity {

    //model for logged User, object in a localStorage
    export interface IUser {
        id?: string;
        username?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        role?: string;
        fullName?: string;
        systemLoginType?: SystemLoginType; //SERVICER: 0,ADVISOR: 1,INVESTOR: 2
        loginStatus?: number;
        systemLoginTypeDesc?: string;
        loginStatusDesc?: string;
        advisor?: domains.IAdvisor;
        pictureUrl?: string;
        lastLogin?: Date;
        impersonated?: boolean;
        impersonatedBy?: string;
        impersonatedByRole?: string;

        // persistent: is two-factor authentication switched on for this login
        twoFactorEnabled?: boolean;

        /// How this login may authenticate: 0 password only, 1 password or Salesforce,
        /// 2 Salesforce only. See advisorop.identity.SsoModeOption.
        ssoMode?: number;

        //two factor
        twofAskMethod?: boolean;
        twofEmails?: string[];
        twofPhones?: string[];
        twof?: boolean;

        // we get this after user log in
        sessionLifeTime?: number;
        sessionWarning?: number;
        sessionCheckEvery?: number;
    }
    /// One place a one-time passcode can be delivered, masked for display.
    export interface ITwoFactorAddress {
        method?: number;       // 1 phone, 2 email
        methodDesc?: string;
        address?: string;      // masked
    }

    /// A login's two-factor state and the contact details a code could go to. Serves both the
    /// signed-in user's own profile panel and an administrator looking at somebody else's login.
    export interface ITwoFactorSetup {
        twoFactorEnabled?: boolean;
        email?: string;
        canAddEmail?: boolean;
        phone?: string;        // formatted for display only
        canSetPhone?: boolean;
        canEnable?: boolean;

        /// Every address sign-in would actually offer, masked.
        addresses?: ITwoFactorAddress[];
    }

    /// -------------------------------------------------------------------------------------------
    /// Client-side validation for the contact details a one-time passcode can be delivered to.
    ///
    /// A deliberate mirror of ContactValidation on the server, which remains the authority -
    /// nothing here can be skipped by posting directly. The point of repeating it is that the user
    /// finds out about a bad number while they are looking at the field, not after a round trip.
    /// Where the two ever disagree, the server wins and its message is what gets shown.
    ///
    /// Lives in domains rather than beside either screen that uses it: the profile panel and the
    /// servicer edit screen both validate the same values, and two copies would drift.
    /// -------------------------------------------------------------------------------------------
    export class ContactValidator {

        private static readonly TOLL_FREE = ["800", "833", "844", "855", "866", "877", "888"];

        /// Returns an error message, or null when the number is acceptable.
        static usPhone(raw: string): string {
            if (!raw || !raw.trim()) {
                return "Enter a mobile number.";
            }

            let digits = raw.replace(/\D/g, "");

            if (digits.length === 11 && digits.charAt(0) === "1") {
                digits = digits.substring(1);
            } else if (digits.length === 11) {
                return "Only US numbers are accepted.";
            }

            if (digits.length < 10) { return "A US mobile number has 10 digits."; }
            if (digits.length > 10) { return "That is too many digits for a US number."; }

            const npa = digits.substring(0, 3);
            const nxx = digits.substring(3, 6);
            const line = digits.substring(6, 10);

            if (/^(\d){9}$/.test(digits)) { return "That is not a real phone number."; }

            if (npa.charAt(0) < "2") { return "An area code cannot start with 0 or 1."; }
            if (npa.charAt(1) === "1" && npa.charAt(2) === "1") { return "That is a service code, not an area code."; }
            if (npa.indexOf("37") === 0 || npa.indexOf("96") === 0) { return "That area code is not in service."; }
            if (ContactValidator.TOLL_FREE.indexOf(npa) >= 0) { return "Enter a mobile number, not a toll-free number."; }
            if (npa === "900") { return "Enter a mobile number."; }

            if (nxx.charAt(0) < "2") { return "That is not a valid US number."; }
            if (nxx.charAt(1) === "1" && nxx.charAt(2) === "1") { return "That is not a valid US number."; }

            if (nxx === "555") {
                const lineNumber = parseInt(line, 10);
                if (lineNumber >= 100 && lineNumber <= 199) {
                    return "That is a reserved number and cannot receive messages.";
                }
            }

            return null;
        }

        /// Returns an error message, or null when the address is acceptable.
        static email(raw: string): string {
            if (!raw || !raw.trim()) { return "Enter an email address."; }

            const value = raw.trim();

            if (/\s/.test(value)) { return "An email address cannot contain spaces."; }
            if (value.length > 254) { return "That email address is too long."; }

            const at = value.indexOf("@");
            if (at < 0 || at !== value.lastIndexOf("@")) {
                return "Enter an email address in the form name@example.com.";
            }

            const local = value.substring(0, at);
            const domain = value.substring(at + 1);

            if (local.length === 0 || local.length > 64) { return "The part before the @ is missing or too long."; }
            if (domain.length === 0) { return "The part after the @ is missing."; }

            if (!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) {
                return "The part before the @ contains a character that is not allowed.";
            }
            if (local.charAt(0) === "." || local.charAt(local.length - 1) === "." || local.indexOf("..") >= 0) {
                return "The part before the @ cannot start, end, or run two dots together.";
            }

            if (domain.charAt(0) === "[") { return "Enter a domain name, not an IP address."; }

            const labels = domain.split(".");
            if (labels.length < 2) { return "The domain needs a suffix, for example example.com."; }

            for (const label of labels) {
                if (label.length === 0 || label.length > 63) { return "That domain is not valid."; }
                if (label.charAt(0) === "-" || label.charAt(label.length - 1) === "-") {
                    return "A domain part cannot start or end with a hyphen.";
                }
                if (!/^[A-Za-z0-9-]+$/.test(label)) { return "That domain is not valid."; }
            }

            const tld = labels[labels.length - 1];
            if (tld.length < 2 || !/^[A-Za-z]+$/.test(tld)) { return "That domain suffix is not valid."; }

            return null;
        }

        /// Presentation only. What gets stored is bare digits - see ContactValidation on the server.
        static formatUsPhone(raw: string): string {
            if (!raw) { return raw; }

            let digits = raw.replace(/\D/g, "");
            if (digits.length === 11 && digits.charAt(0) === "1") {
                digits = digits.substring(1);
            }
            if (digits.length !== 10) { return raw; }

            return "(" + digits.substring(0, 3) + ") " + digits.substring(3, 6) + "-" + digits.substring(6, 10);
        }
    }

    export interface IRole {
        id?: string;
        name?: string;
    }

    ///model for a Login user form
    export interface ILoginFormModel {
        username?: string;        
        password?: string;
        token?: string;
        rememberMe?: boolean;
        twofMethod?: number;
        twofAddressIndex?: number;
        privateMachine?: boolean;
        returnUrl?: string;
        isMeetingLogin?: boolean;
        meetingGuid?: string;
        participantGuid?: string;
    }

    export class LoginFormModel implements ILoginFormModel {
        public username: string;        
        public password: string;
        public rememberMe: boolean;
        public returnUrl: string;
    }

    export class LoginRet {
        public retCode: number;
        public twofEmails?: string[];
        public twofPhones?: string[];
    }

    /// What the login screen is told about Salesforce sign-in. Everything else the flow needs -
    /// the consumer key, the authorize URL - stays on the server.
    export interface ISsoConfig {
        enabled: boolean;
        buttonText?: string;
    }

    /// One AdvisorOp login on offer when a single Salesforce identity matched more than one.
    /// The reference is opaque and only means anything to the ticket that issued it.
    export interface ISsoAccountChoice {
        reference: string;
        userName: string;
        roleLabel: string;
    }

    export interface ISsoChoices {
        nonce: string;
        choices: ISsoAccountChoice[];
    }

    //model - ForgetPassword
    export interface IForgotPasswordModel {
        username: string;
    }

    export class ForgotPasswordModel implements IForgotPasswordModel {
        username: string;
    }

    //model - ResetPassword
    export interface IResetPasswordModel {
        userId: string;
        password: string;
        confirmPassword: string;
        code: string;
    }

    export class ResetPasswordModel implements IResetPasswordModel {
        userId: string;
        password: string;
        confirmPassword: string;
        code: string
    }  

    //model - ChangePassword
    export interface IChangePasswordModel {
        oldPassword: string;
        password: string;
        confirmPassword: string;
    }

    export class ChangePasswordModel implements IChangePasswordModel {
        oldPassword: string;
        password: string;
        confirmPassword: string;
    }

    //model for setup account for the first time
    export interface ISetupAccountModel extends IResetPasswordModel {
        //maybe we need additional fields FirstName, LastName ...
    }

    export class SetupAccountModel extends ResetPasswordModel implements ISetupAccountModel {
    }





    //model for user registration form
    export interface IRegisterFormModel {
        email: string;
        password: string;
        passwordConfirmation: string;
        firstName: string;
        lastName: string;
        address: string;
        cellPhone: string;
    }


}